'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { getModels, type Model } from '../../api/spareparts-api';

export const normalizeSpaces = (text: string) => text.replace(/\s+/g, ' ');
export const getModelAftername = (node: Model) =>
  [node.year, node.specifications, node.articleNumber]
    .filter(Boolean)
    .map(value => `<${value}>`)
    .join(' ');

interface ModelNode extends Model {
  children?: ModelNode[];
  childrenLoaded?: boolean;
  isLoading?: boolean;
}

interface ModelSearchBoxProps {
  onSelect?: (model: Model) => void;
  className?: string;
  selectedModel?: Model | null;
  initialSearchQuery?: string;
  brandId?: string;
  expandedNodes?: Set<string>;
  setExpandedNodes?: (nodes: Set<string>) => void;
  persistentExpandedNodes?: Set<string>;
  setPersistentExpandedNodes?: (nodes: Set<string>) => void;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  modelTree?: ModelNode[];
  setModelTree?: (tree: ModelNode[]) => void;
  shouldSearch?: boolean;
  setShouldSearch?: (search: boolean) => void;
}

function updateModelInTree(
  nodes: ModelNode[],
  id: string,
  updates: Partial<ModelNode>,
): ModelNode[] {
  return nodes.map(node =>
    node.id === id
      ? { ...node, ...updates }
      : node.children
        ? { ...node, children: updateModelInTree(node.children, id, updates) }
        : node,
  );
}

// The official API has no full-text endpoint. Filter only branches already opened by the user.
function filterLoadedModels(nodes: ModelNode[], query: string): ModelNode[] {
  if (!query) return nodes;
  return nodes.flatMap(node => {
    const label = normalizeSpaces(
      [node.description, node.articleNumber, node.year, node.specifications]
        .filter(Boolean)
        .join(' '),
    ).toLocaleLowerCase();
    if (label.includes(query)) return [node];
    const children = filterLoadedModels(node.children || [], query);
    return children.length ? [{ ...node, children }] : [];
  });
}

export function ModelSearchBox({
  onSelect,
  className,
  selectedModel,
  initialSearchQuery,
  brandId,
  expandedNodes: propExpandedNodes,
  setExpandedNodes: propSetExpandedNodes,
  persistentExpandedNodes: propPersistentExpandedNodes,
  setPersistentExpandedNodes: propSetPersistentExpandedNodes,
  searchQuery: propSearchQuery,
  setSearchQuery: propSetSearchQuery,
  modelTree: propModelTree,
  setModelTree: propSetModelTree,
}: ModelSearchBoxProps) {
  const apiKeys = useGlobalStore(state => state.apiKeys);
  const apiKey = (apiKeys as any)?.spareparts || '';
  const [localExpandedNodes, setLocalExpandedNodes] = useState<Set<string>>(new Set());
  const [localPersistentExpandedNodes, setLocalPersistentExpandedNodes] = useState<Set<string>>(
    new Set(),
  );
  const [localSearchQuery, setLocalSearchQuery] = useState(initialSearchQuery || '');
  const [localModelTree, setLocalModelTree] = useState<ModelNode[]>([]);
  const [loadingNodes, setLoadingNodes] = useState<Set<string>>(new Set());
  const [nodeErrors, setNodeErrors] = useState<Record<string, string>>({});
  const expandedNodes = propExpandedNodes ?? localExpandedNodes;
  const setExpandedNodes = propSetExpandedNodes ?? setLocalExpandedNodes;
  const persistentExpandedNodes = propPersistentExpandedNodes ?? localPersistentExpandedNodes;
  const setPersistentExpandedNodes =
    propSetPersistentExpandedNodes ?? setLocalPersistentExpandedNodes;
  const searchQuery = propSearchQuery ?? localSearchQuery;
  const setSearchQuery = propSetSearchQuery ?? setLocalSearchQuery;
  const modelTree = propModelTree ?? localModelTree;
  const setModelTree = propSetModelTree ?? setLocalModelTree;
  const currentTree = useRef(modelTree);
  const activeRequests = useRef(new Set<string>());
  const mounted = useRef(true);
  currentTree.current = modelTree;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const {
    data: topLevelModels,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['models', 'top-level', brandId, apiKey],
    queryFn: async () => {
      const result = await getModels({ body: { parentPath: '/', depth: 1, brandId }, apiKey });
      if (!result.success || !Array.isArray(result.data)) {
        throw new Error(result.message || 'Modely sa nepodarilo načítať. Skúste to znova.');
      }
      return result.data;
    },
    enabled: !!brandId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (topLevelModels && !modelTree.length) {
      setModelTree(topLevelModels.map(model => ({ ...model, children: [] })));
    }
  }, [topLevelModels, modelTree.length, setModelTree]);

  const loadChildren = async (node: ModelNode) => {
    if (node.childrenLoaded || node.children?.length || activeRequests.current.has(node.id)) return;
    if (!node.modelStructurePath) {
      setNodeErrors(previous => ({
        ...previous,
        [node.id]: 'Katalóg nevrátil cestu k tejto skupine modelov.',
      }));
      return;
    }
    activeRequests.current.add(node.id);
    setLoadingNodes(new Set(activeRequests.current));
    setNodeErrors(previous => ({ ...previous, [node.id]: '' }));
    try {
      const result = await getModels({
        body: { parentPath: node.modelStructurePath, depth: 1, brandId },
        apiKey,
      });
      if (!mounted.current) return;
      if (!result.success || !Array.isArray(result.data)) {
        throw new Error(result.message || 'Modely sa nepodarilo načítať. Skúste to znova.');
      }
      const children = result.data
        .filter(model => model.id !== node.id)
        .map(model => ({ ...model, children: [] }));
      const nextTree = updateModelInTree(currentTree.current, node.id, {
        children,
        childrenLoaded: true,
      });
      currentTree.current = nextTree;
      setModelTree(nextTree);
    } catch (failure) {
      if (mounted.current) {
        setNodeErrors(previous => ({
          ...previous,
          [node.id]: failure instanceof Error ? failure.message : 'Modely sa nepodarilo načítať.',
        }));
      }
    } finally {
      activeRequests.current.delete(node.id);
      if (mounted.current) setLoadingNodes(new Set(activeRequests.current));
    }
  };

  const toggleNode = (node: ModelNode) => {
    const nextExpanded = new Set(expandedNodes);
    if (nextExpanded.has(node.id)) {
      nextExpanded.delete(node.id);
    } else {
      nextExpanded.add(node.id);
      setPersistentExpandedNodes(new Set([...persistentExpandedNodes, node.id]));
      void loadChildren(node);
    }
    setExpandedNodes(nextExpanded);
  };

  const normalizedQuery = normalizeSpaces(searchQuery).trim().toLocaleLowerCase();
  const visibleModels = filterLoadedModels(modelTree, normalizedQuery);

  const renderNode = (node: ModelNode): ReactNode => {
    const hasChildren = node.hasChildren || !!node.children?.length;
    const isExpanded = expandedNodes.has(node.id) || (!!normalizedQuery && !!node.children?.length);
    const nodeError = nodeErrors[node.id];
    return (
      <div key={node.id} className="w-full">
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-2 rounded p-2 text-left text-sm hover:bg-primary/10',
            !hasChildren && 'font-bold',
            selectedModel?.id === node.id && 'bg-primary/20 text-primary',
          )}
          disabled={loadingNodes.has(node.id)}
          onClick={() => {
            if (hasChildren) toggleNode(node);
            else if (node.articleNumber) onSelect?.(node);
          }}
        >
          {loadingNodes.has(node.id) ? (
            <Spinner className="h-4 w-4 shrink-0" />
          ) : hasChildren ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 shrink-0" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0" />
            )
          ) : (
            <span className="w-4 shrink-0" />
          )}
          <span>
            {normalizeSpaces(node.description || node.articleNumber || 'Model bez názvu')}
            {!hasChildren && getModelAftername(node) && ` ${getModelAftername(node)}`}
          </span>
        </button>
        {nodeError && (
          <div role="alert" className="ml-6 space-y-2 p-2 text-sm text-red-700">
            <p>{nodeError}</p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void loadChildren(node)}
            >
              Skúsiť znova
            </Button>
          </div>
        )}
        {hasChildren && isExpanded && (
          <div className="ml-4 border-l border-gray-200">
            {node.children?.map(renderNode)}
            {node.childrenLoaded && !node.children?.length && (
              <p className="p-2 text-sm text-gray-500">V tejto skupine nie sú dostupné modely.</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={cn(
        'model-search-box flex max-h-[50vh] flex-col rounded-lg border border-gray-200 bg-white',
        className,
      )}
    >
      <div className="shrink-0 border-b border-gray-200 bg-gray-50 p-3">
        <h3 className="text-sm font-medium text-gray-700">Vyberte model</h3>
        <p className="mt-1 text-xs text-gray-500">Rozbaľte skupinu a vyberte konkrétny model.</p>
      </div>
      <div className="shrink-0 space-y-2 border-b border-gray-200 p-3">
        <Input
          aria-label="Filtrovať načítané modely"
          placeholder="Filtrovať načítané modely..."
          value={searchQuery}
          onChange={event => setSearchQuery(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') event.preventDefault();
          }}
        />
        <p className="text-xs text-gray-500">
          Filter prehľadáva iba načítané skupiny a modely. Ďalšie modely sprístupníte rozbalením
          skupiny.
        </p>
        {searchQuery && (
          <Button type="button" size="sm" variant="ghost" onClick={() => setSearchQuery('')}>
            Zrušiť filter
          </Button>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-2">
        {!brandId ? (
          <p className="p-4 text-center text-sm text-gray-500">Najprv vyberte značku.</p>
        ) : isLoading ? (
          <div className="flex items-center justify-center gap-2 p-4 text-sm text-gray-500">
            <Spinner className="h-5 w-5" /> Načítavam modely...
          </div>
        ) : error ? (
          <div role="alert" className="space-y-3 p-4 text-sm text-red-700">
            <p>{error.message}</p>
            <Button type="button" variant="outline" onClick={() => void refetch()}>
              Skúsiť znova
            </Button>
          </div>
        ) : visibleModels.length ? (
          visibleModels.map(renderNode)
        ) : (
          <p className="p-4 text-center text-sm text-gray-500">
            {normalizedQuery
              ? 'V načítaných modeloch sa nenašla zhoda. Zrušte filter a rozbaľte ďalšiu skupinu.'
              : 'Pre túto značku momentálne nie sú dostupné modely.'}
          </p>
        )}
      </div>
    </div>
  );
}
